"use client";

import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { AnimatePresence, motion } from "motion/react";
import { Check, ChevronDown } from "lucide-react";
import { cn } from "../lib/cn";
import { springs, durations, easings } from "../lib/motion";
import { useReducedMotion } from "../hooks/useReducedMotion";
import { celebrate, type CelebrateIntensity } from "../lib/celebrate";
import { Progress } from "../ui/progress";
import { Skeleton } from "../primitives/Skeleton";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../ui/collapsible";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../primitives/Tooltip";

/**
 * SetupChecklist: one collapsible onboarding checklist with an animated
 * progress bar, motion-driven rows, a two-tap dismiss guard, and a
 * celebrate-on-complete moment.
 *
 * Consolidates the two copy-paste forks from Creator Studio / FE-React
 * (`TeamSetupChecklist`, `StarterChecklist`) into one configurable component
 * built on primitives the library already ships:
 * - `Progress` (Radix) for the determinate bar,
 * - `Collapsible` (Radix) for the keyboard-accessible expand / collapse,
 * - `Tooltip` for the toggle hint,
 * - `celebrate()` for the confetti when every task lands, and
 * - `motion/react` `AnimatePresence` for the row enter / exit, gated on
 *   `useReducedMotion` with a static fallback.
 *
 * Row motion note: this composes `AnimatePresence` directly rather than the
 * `AnimatedList` component because `AnimatedList` hardcodes a per-row
 * `rounded-2xl border bg-card` card chrome on its row wrapper that `renderItem`
 * cannot override, which would box every task in its own card. The donor
 * checklists (and this port) want flat rows, so we drive the same
 * `AnimatePresence` slide-in/out that `AnimatedList` wraps, with flat
 * donor-matching row styling.
 *
 * Router-free by design: incomplete rows that carry an `href` are wrapped in a
 * consumer-supplied `linkComponent` (the app's `<Link>`); the library imports
 * no router. For full control over a row, pass `renderRow`.
 *
 * Brand tokens only. The donors' navy-to-blue gradient and
 * `text-gray-*` washes are replaced with `bg-primary` / `text-foreground` /
 * `text-muted-foreground` / `border-border` / `bg-card`. Light theme only.
 *
 * @example
 *   <SetupChecklist
 *     title="Jumpstart Your AISquare Journey"
 *     description="Complete this checklist to unlock the full AISquare experience"
 *     tasks={tasks}
 *     linkComponent={Link}        // your router Link; incomplete rows navigate
 *     onDismiss={dismissChecklist}
 *   />
 *
 *   // Server-computed completion (a la Starter's `checklist_completion`):
 *   <SetupChecklist tasks={tasks} percentComplete={profile.checklist_completion} />
 */

/** A single checklist row. Map your API shape onto this neutral type. */
export interface SetupChecklistTask {
  /** Stable identifier, used as the React key and the AnimatedList item id. */
  id: string;
  /** Row text. */
  label: string;
  /** Whether the task is done. Completed rows get a filled circle + strike-through and are non-interactive. */
  completed: boolean;
  /**
   * Where an incomplete row should navigate. When set together with
   * `linkComponent`, the row renders as that link; otherwise it is a plain
   * non-navigating row.
   */
  href?: string;
  /** Optional one-line subtext under the label. */
  description?: string;
}

/** Render-prop context handed to `renderRow` for fully custom rows. */
export interface SetupChecklistRowContext {
  task: SetupChecklistTask;
  /** The default-styled row content (check-circle + label). Wrap or return as-is. */
  defaultContent: React.ReactNode;
}

export const setupChecklistVariants = cva(
  [
    "flex flex-col overflow-hidden rounded-2xl border border-border bg-card text-card-foreground",
    "shadow-[0_1px_2px_-1px_rgb(0_0_0_/_0.04),_0_4px_16px_-8px_rgb(0_0_0_/_0.06)]",
  ].join(" "),
  {
    variants: {
      size: {
        /** Tighter padding, for sidebars / dense surfaces. */
        compact: "[--sc-pad:1rem] [--sc-row-pad:0.5rem]",
        /** Default roomy padding, matches the donor dashboard cards. */
        comfortable: "[--sc-pad:1.25rem] [--sc-row-pad:0.75rem]",
      },
    },
    defaultVariants: {
      size: "comfortable",
    },
  },
);

export interface SetupChecklistProps
  extends
    Omit<React.HTMLAttributes<HTMLDivElement>, "title" | "onChange">,
    VariantProps<typeof setupChecklistVariants> {
  /** The checklist rows. Rendered in the given order (sort upstream if needed). */
  tasks: SetupChecklistTask[];
  /** Heading above the card. Optional, omit for a heading-less embed. */
  title?: React.ReactNode;
  /** Sub-line inside the header. */
  description?: React.ReactNode;
  /**
   * Override the derived `completed / total` percentage with a server-computed
   * value (0-100), matching Starter's `checklist_completion`. The row checks
   * still come from each task's `completed` flag.
   */
  percentComplete?: number;
  /** Fired when the dismiss control is confirmed. Omit to hide the dismiss button. */
  onDismiss?: () => void;
  /**
   * Require two taps to dismiss (the first arms a "Click again to confirm"
   * state). Guards against an accidental one-click dismiss of onboarding.
   * Default `true`.
   */
  confirmDismiss?: boolean;
  /** Label for the dismiss button. Default "Dismiss Checklist". */
  dismissLabel?: React.ReactNode;
  /** Fired whenever the collapsed state changes. */
  onCollapseChange?: (collapsed: boolean) => void;
  /** Start collapsed. Default `false`. */
  defaultCollapsed?: boolean;
  /**
   * The consumer's router link component, used to wrap incomplete rows that
   * carry an `href`. Receives `{ to, href, className, children }` so it works
   * with both `react-router` (`to`) and anchor-style (`href`) links. The
   * library imports no router. Ignored when `renderRow` is provided.
   */
  linkComponent?: React.ElementType;
  /**
   * Full row escape hatch. Receives the task plus the default-styled content;
   * return whatever wrapper you need. Overrides `linkComponent`.
   */
  renderRow?: (ctx: SetupChecklistRowContext) => React.ReactNode;
  /** Fire `celebrate()` once when progress reaches 100%. Default `true`. */
  /**
   * The task list has not arrived yet — renders the card with placeholder rows.
   *
   * This one MUST outrank the empty guard rather than sit after it, and the
   * reason is structural: an unloaded checklist has `tasks: []`, which the
   * guard below answers with `null`. Without this branch the card is absent
   * from the layout during the fetch and then pops in at full height, shoving
   * everything under it down — on a dashboard, usually the thing the user was
   * already reaching for.
   */
  loading?: boolean;
  /** Placeholder row count while `loading`. Default 3. */
  loadingRows?: number;
  celebrateOnComplete?: boolean;
  /** Confetti intensity for the completion moment. Default "burst". */
  celebrateIntensity?: CelebrateIntensity;
}

const DEFAULT_TITLE = undefined;
const DEFAULT_DESCRIPTION = undefined;

/** Check-circle + label, the shared inner content for every row state. */
function RowContent({ task, reduced }: { task: SetupChecklistTask; reduced: boolean }) {
  return (
    <div className="flex items-start gap-3">
      <span
        className={cn(
          "flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full",
          task.completed
            ? "bg-primary text-primary-foreground shadow-sm"
            : "border-2 border-border bg-background",
        )}
        aria-hidden
      >
        {task.completed ? (
          reduced ? (
            <Check className="h-3.5 w-3.5" />
          ) : (
            <motion.span
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={springs.snappy}
              className="flex items-center justify-center"
            >
              <Check className="h-3.5 w-3.5" />
            </motion.span>
          )
        ) : null}
      </span>
      <span className="flex-1 pt-0.5 text-left">
        <span
          className={cn(
            "block text-sm",
            task.completed
              ? "font-normal text-muted-foreground line-through"
              : "font-semibold text-foreground",
          )}
        >
          {task.label}
        </span>
        {task.description ? (
          <span className="mt-0.5 block text-xs text-muted-foreground">{task.description}</span>
        ) : null}
      </span>
    </div>
  );
}

const ROW_SKELETON_WIDTHS = ["w-1/2", "w-2/3", "w-[45%]", "w-[58%]"] as const;

export const SetupChecklist = React.forwardRef<HTMLDivElement, SetupChecklistProps>(
  (
    {
      className,
      size,
      tasks,
      title = DEFAULT_TITLE,
      description = DEFAULT_DESCRIPTION,
      percentComplete,
      onDismiss,
      confirmDismiss = true,
      dismissLabel = "Dismiss Checklist",
      onCollapseChange,
      defaultCollapsed = false,
      linkComponent,
      renderRow,
      loading = false,
      loadingRows = 3,
      celebrateOnComplete = true,
      celebrateIntensity = "burst",
      ...rest
    },
    ref,
  ) => {
    const reduced = useReducedMotion() ?? false;
    const [collapsed, setCollapsed] = React.useState(defaultCollapsed);
    const [dismissArmed, setDismissArmed] = React.useState(false);

    const completedCount = tasks.filter((t) => t.completed).length;
    const total = tasks.length;
    const derivedPercent = total > 0 ? (completedCount / total) * 100 : 0;
    const percent = percentComplete ?? derivedPercent;
    const isComplete = total > 0 && percent >= 100;

    // Fire the celebration once on the transition into 100%, never on mount of
    // an already-complete list and never again on re-render. The ref tracks the
    // last-seen completeness so re-completing after a dip re-fires correctly.
    const wasCompleteRef = React.useRef(isComplete);
    React.useEffect(() => {
      if (!celebrateOnComplete) {
        wasCompleteRef.current = isComplete;
        return;
      }
      if (isComplete && !wasCompleteRef.current) {
        // celebrate() is SSR-safe and no-ops under prefers-reduced-motion.
        celebrate({ intensity: celebrateIntensity });
      }
      wasCompleteRef.current = isComplete;
    }, [isComplete, celebrateOnComplete, celebrateIntensity]);

    // Collapse the list automatically once everything is done (the checklist
    // has served its purpose), but only on the completing transition so the
    // user can still re-open it. Honors an explicit collapse toggle afterward.
    const autoCollapsedRef = React.useRef(false);
    React.useEffect(() => {
      if (isComplete && !autoCollapsedRef.current) {
        autoCollapsedRef.current = true;
        setCollapsed(true);
        onCollapseChange?.(true);
      }
      if (!isComplete) autoCollapsedRef.current = false;
    }, [isComplete, onCollapseChange]);

    // Radix Collapsible is the source of truth for open/closed; sync our
    // `collapsed` mirror from its callback so the chevron + auto-collapse and
    // the Radix state never drift.
    const handleOpenChange = React.useCallback(
      (open: boolean) => {
        const next = !open;
        setCollapsed(next);
        onCollapseChange?.(next);
      },
      [onCollapseChange],
    );

    const handleDismiss = React.useCallback(() => {
      if (confirmDismiss && !dismissArmed) {
        setDismissArmed(true);
        return;
      }
      onDismiss?.();
    }, [confirmDismiss, dismissArmed, onDismiss]);

    const renderTaskRow = React.useCallback(
      (task: SetupChecklistTask): React.ReactNode => {
        const defaultContent = <RowContent task={task} reduced={reduced} />;

        if (renderRow) {
          return renderRow({ task, defaultContent });
        }

        const rowClass = cn(
          "relative z-10 block w-full rounded-xl p-[var(--sc-row-pad)] transition-[background-color,box-shadow,transform] duration-200 ease-smooth-out",
          task.completed
            ? "bg-muted/40"
            : "hover:-translate-y-0.5 hover:bg-primary/5 hover:shadow-md",
        );

        // Incomplete rows with an href navigate via the injected link
        // component; the library never imports a router itself.
        if (!task.completed && task.href && linkComponent) {
          const LinkComp = linkComponent;
          return (
            <LinkComp to={task.href} href={task.href} className={rowClass}>
              {defaultContent}
            </LinkComp>
          );
        }

        return <div className={rowClass}>{defaultContent}</div>;
      },
      [linkComponent, renderRow, reduced],
    );

    // Ahead of the empty guard on purpose — see `loading`.
    if (loading) {
      return (
        <div
          ref={ref}
          className={cn(setupChecklistVariants({ size }), className)}
          data-slot="setup-checklist"
          aria-busy="true"
          aria-label="Loading checklist"
          {...rest}
        >
          <div className="border-b border-border p-[var(--sc-pad)]">
            <div className="mb-3 flex items-center justify-between gap-3">
              <Skeleton variant="text" animation="shimmer" className="h-3.5 w-2/5" />
              <Skeleton className="h-5 w-5 rounded-full" />
            </div>
            {/* Same 8px bar the real Progress paints, so the header does not
                change height when the counts resolve. */}
            <Skeleton className="h-2 w-full rounded-full" />
          </div>
          <ul className="flex flex-col gap-1 px-[var(--sc-pad)] py-2">
            {Array.from({ length: Math.max(1, loadingRows) }).map((_, i) => (
              <li
                key={i}
                aria-hidden="true"
                className="flex items-start gap-3 p-[var(--sc-row-pad)]"
              >
                {/* h-6 w-6 circle + the label/description pair: the exact
                    geometry of a real row, so nothing reflows on arrival. */}
                <Skeleton className="h-6 w-6 flex-shrink-0 rounded-full" />
                <div className="flex-1 space-y-1.5 pt-0.5">
                  <Skeleton
                    variant="text"
                    animation="shimmer"
                    className={cn("h-3.5", ROW_SKELETON_WIDTHS[i % ROW_SKELETON_WIDTHS.length])}
                  />
                  <Skeleton variant="text" animation="shimmer" className="h-3 w-1/3" />
                </div>
              </li>
            ))}
          </ul>
        </div>
      );
    }

    // Empty-guard: parent owns visibility, so render nothing when there are no
    // tasks (preserves Starter's `null` return).
    if (total === 0) return null;

    const toggleLabel = collapsed ? "Expand checklist" : "Collapse checklist";

    const card = (
      <div
        ref={ref}
        className={cn(setupChecklistVariants({ size }), className)}
        data-slot="setup-checklist"
        {...rest}
      >
        <Collapsible open={!collapsed} onOpenChange={handleOpenChange}>
          {/* Header: copy + progress + collapse toggle */}
          <div className="border-b border-border p-[var(--sc-pad)]">
            <div className="mb-3 flex items-center justify-between gap-3">
              {description ? (
                <p className="min-w-0 flex-1 text-sm font-medium text-muted-foreground">
                  {description}
                </p>
              ) : (
                <span className="flex-1" />
              )}

              <TooltipProvider delayDuration={200}>
                <Tooltip>
                  {/* `CollapsibleTrigger` (stock) is a plain function component
                      and can't receive a forwarded ref, so it must sit OUTSIDE
                      the ref-passing `TooltipTrigger asChild`. The chain
                      CollapsibleTrigger(asChild) > TooltipTrigger(asChild) >
                      <button> lands every ref on the real DOM button while both
                      Radix wrappers merge their props (aria-expanded, data-state,
                      tooltip handlers) onto that single node. */}
                  <CollapsibleTrigger asChild>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        aria-label={toggleLabel}
                        className="rounded-lg p-2 text-muted-foreground transition-colors duration-200 hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      >
                        <ChevronDown
                          className={cn(
                            "h-5 w-5 transition-transform duration-200 ease-smooth-out motion-reduce:transition-none",
                            !collapsed && "rotate-180",
                          )}
                          aria-hidden
                        />
                      </button>
                    </TooltipTrigger>
                  </CollapsibleTrigger>
                  <TooltipContent side="left">{toggleLabel}</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Progress
                </span>
                <span className="text-sm font-bold text-muted-foreground">
                  {completedCount} of {total} complete
                </span>
              </div>
              <div className="relative overflow-hidden rounded-full">
                <Progress
                  value={percent}
                  aria-label={`${completedCount} of ${total} tasks complete`}
                />
                {/* Decorative shimmer sweep over the filled track. The preset's
                    `animate-shimmer` is auto-suppressed under reduced motion via
                    the base `motion-reduce` rule, so no manual gate is needed. */}
                {percent > 0 ? (
                  <span
                    aria-hidden
                    className="pointer-events-none absolute inset-y-0 left-0 rounded-full bg-[linear-gradient(90deg,transparent,hsl(var(--primary-foreground)/0.35),transparent)] bg-[length:200%_100%] animate-shimmer motion-reduce:animate-none motion-reduce:bg-none"
                    style={{ width: `${percent}%` }}
                  />
                ) : null}
              </div>
            </div>
          </div>

          {/* Rows: collapse via Radix, per-row enter/exit via AnimatePresence.
              Reduced-motion users get an instant fade (no slide/height tween),
              mirroring AnimatedList's own reduced-motion behavior. */}
          <CollapsibleContent className="overflow-hidden data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down">
            <ul className="flex flex-col gap-1 px-[var(--sc-pad)] py-2">
              <AnimatePresence initial={false} mode="popLayout">
                {tasks.map((task) => (
                  <motion.li
                    key={task.id}
                    layout={!reduced}
                    initial={reduced ? { opacity: 0 } : { opacity: 0, height: 0, y: -12 }}
                    animate={{ opacity: 1, height: "auto", y: 0 }}
                    exit={reduced ? { opacity: 0 } : { opacity: 0, height: 0, y: 12 }}
                    transition={
                      reduced
                        ? { duration: 0 }
                        : { duration: durations.surface, ease: easings.smoothOut }
                    }
                    style={{ overflow: "visible" }}
                  >
                    {renderTaskRow(task)}
                  </motion.li>
                ))}
              </AnimatePresence>
            </ul>
          </CollapsibleContent>
        </Collapsible>

        {/* Dismiss, outside the collapsible so it stays reachable when closed */}
        {onDismiss ? (
          <div className="flex justify-center border-t border-border px-[var(--sc-pad)] pb-3 pt-2">
            <button
              type="button"
              onClick={handleDismiss}
              className="cursor-pointer rounded-md px-4 py-2 text-sm font-medium text-muted-foreground transition-colors duration-200 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {dismissArmed ? "Click again to confirm" : dismissLabel}
            </button>
          </div>
        ) : null}
      </div>
    );

    if (!title) return card;

    return (
      <div className="flex flex-col">
        <h2 className="mb-3 text-xl font-semibold text-foreground">{title}</h2>
        {card}
      </div>
    );
  },
);
SetupChecklist.displayName = "SetupChecklist";
